/**
 * @file SiteStarterButton.tsx
 * @description «Собрать стартовый сайт ещё раз» во вкладке «Обзор»
 * карточки магазина (phase19.28 этап А; решение пользователя 2026-10-24;
 * uiux-starter-site §7: повтор идемпотентен, отметка в журнале
 * `site_starter.apply`). Право `PLATFORM_MANAGE_SHOPS`; подтверждение
 * окном кабинета, итог — toast, затем карточка перечитывается.
 */

import { LayoutTemplate } from "lucide-react";
import { useState } from "react";

import { platformShops, type SiteStarterApplyResult } from "../../../lib/api-platform-shops";
import { apiErrorText } from "../../../lib/platform/shop-errors";
import { siteStarterText } from "../../../lib/platform/shop-format";
import { Button } from "../../ui/button";
import { ConfirmDialog } from "../../ui/ConfirmDialog";
import { useNotify } from "../../ui/NotifyProvider";
import { useConfirm } from "../../ui/use-confirm";

interface Props {
  shopId: string;
  shopName: string;
  /** Ответ сборки — экран итога перерисовывает строку, «Обзор» перечитывает карточку. */
  onApplied: (result: SiteStarterApplyResult) => void;
}

export function SiteStarterButton({ shopId, shopName, onApplied }: Props) {
  const notify = useNotify();
  const confirmApi = useConfirm();
  const [busy, setBusy] = useState(false);

  async function apply() {
    const ok = await confirmApi.confirm({
      title: `Собрать стартовый сайт «${shopName}» ещё раз?`,
      description:
        "Страницы стартового сайта соберутся из шаблона платформы. Повтор безопасен: то, что уже собрано, не задваивается. Сайт останется закрытым до запуска.",
      action: "Собрать",
    });
    if (!ok) return;
    setBusy(true);
    try {
      const result = await platformShops.applySiteStarter(shopId);
      const outcome = siteStarterText(result);
      if (outcome.ok) notify.success(outcome.text);
      else notify.error(outcome.text);
      onApplied(result);
    } catch (err) {
      notify.error(apiErrorText(err, "Стартовый сайт не собрался. Повторите позже"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Button variant="outline" className="self-start" onClick={() => void apply()} loading={busy}>
        <LayoutTemplate className="size-4" aria-hidden />
        Собрать стартовый сайт ещё раз
      </Button>
      <ConfirmDialog request={confirmApi.request} onSettle={confirmApi.settle} onDismiss={confirmApi.dismiss} />
    </>
  );
}
